'use client';

/**
 * Edits after ready in the workspace (edits.html): «Устарело» + «Обновить»
 * next to stale pages, block audio and exports; «Обновить» of a lot of paid
 * work asks first (EditConfirmDialog with the estimate); while the story
 * generates the panel names the lock (edit controls are off meanwhile).
 */
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { getStaleView, requestStaleUpdate } from '../actions/staleView';
import { pipelineKeys } from '../queryKeys';
import { useGenerationLock } from '../hooks/useGenerationLock';
import { StaleBadge, StaleUpdate } from '../edits/components/StaleBadge';
import { EditConfirmDialog } from '../edits/components/EditConfirmDialog';
import { DEFAULT_BIG_EDIT_UNITS } from '../edits/planEdit';
import type { StaleView } from '../edits/editContext.server';
import type { EditPlan } from '../edits/types';
import type { OrderLayersView } from '../order/types';

const units = (e: NonNullable<StaleView['estimate']>) => e.pages + e.covers + e.sheets + e.audioBlocks;

/** The «Обновить» order as an edit plan, for the confirm dialog. */
export function updatePlanOf(view: StaleView): EditPlan {
  return {
    kind: 'redraw_page',
    ed: '',
    rule: 'update',
    autoSteps: [],
    stale: view.items,
    new: [],
    gone: [],
    reused: [],
    confirmRequired: view.estimate && units(view.estimate) >= DEFAULT_BIG_EDIT_UNITS ? ['big_edit'] : [],
    notices: [],
    ...(view.estimate ? { estimate: view.estimate } : {}),
    ...(view.forbiddenReason ? { forbiddenReason: view.forbiddenReason } : {}),
  };
}

export interface StoryStalePanelProps {
  storyId: string;
  order: OrderLayersView | null | undefined;
  /** tests / previews: the view without the server */
  view?: StaleView | null;
}

export function StoryStalePanel({ storyId, order, view: given }: StoryStalePanelProps) {
  const t = useTranslations('stories.edits');
  const qc = useQueryClient();
  const lock = useGenerationLock(order);
  const { data: fetched } = useQuery({
    queryKey: pipelineKeys.stale(storyId),
    queryFn: () => getStaleView(storyId),
    enabled: given === undefined && !!storyId && !!order,
    staleTime: 5_000,
  });
  const view = given !== undefined ? given : fetched;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setPending(true);
    setError(null);
    try {
      const res = await requestStaleUpdate(storyId);
      if (!res.success) setError(res.error);
      setConfirmOpen(false);
      await qc.invalidateQueries({ queryKey: pipelineKeys.stale(storyId) });
    } finally {
      setPending(false);
    }
  };

  const hasStale = !!view && view.items.length > 0;
  const blockedExports = view?.exports.filter((x) => x.blocked) ?? [];
  if (!lock.locked && !hasStale && !blockedExports.length) return null;

  const pages = view?.items.filter((i) => i.artifact === 'page' || i.artifact === 'cover') ?? [];
  const audio = view?.items.filter((i) => i.artifact === 'audio') ?? [];
  const rerecord = audio.filter((i) => i.note === 'rerecord');
  const plan = view ? updatePlanOf(view) : null;
  const forbiddenReason = view?.forbiddenReason ?? (lock.locked ? 'generating' : undefined) ?? undefined;
  const onUpdate = () => (plan && plan.confirmRequired.length ? setConfirmOpen(true) : void run());

  return (
    <section className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-3" data-testid="story-stale-panel">
      {lock.locked && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="story-edit-lock">
          <Lock className="size-3.5" />
          {t('forbidden.generating')}
        </p>
      )}
      {hasStale && (
        <div className="space-y-1.5" data-testid="story-stale-items">
          {pages.length > 0 && <StaleBadge count={pages.length} />}
          {audio.length > 0 && <StaleBadge count={audio.length} note={rerecord.length ? 'rerecord' : undefined} />}
          <StaleUpdate
            count={view!.items.length}
            estimate={view!.estimate ?? undefined}
            forbiddenReason={forbiddenReason}
            pending={pending}
            onUpdate={onUpdate}
          />
        </div>
      )}
      {blockedExports.map((x) => (
        <p key={x.exportId} className="text-xs text-warning" data-testid={`story-export-blocked-${x.kind}`}>
          {x.kind.toUpperCase()}: {t('exportBlocked')}
        </p>
      ))}
      {error && <p className="text-xs text-destructive" data-testid="story-stale-error">{error}</p>}
      {plan && (
        <EditConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} plan={plan} pending={pending} onConfirm={() => void run()} />
      )}
    </section>
  );
}
